import React, { memo, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { getPaymentCountdown } from '../../../utils/paymentCountdown';
import { BA_TIMEZONE, parseBackendSessionDate } from '../../../utils/timezone';
import { AvailabilityDay, timeToMinutes } from './utils';
import { getNowInSelectedTZParts, isoToZoneParts, localTzToUTC } from './timeZoneHelpers';
import { ROW_HEIGHT } from './constants';
import ScheduleOverlay from './ScheduleOverlay';
import SessionItem from './SessionItem';
import CurrentTimeIndicator from './CurrentTimeIndicator';
import type { DragVisual } from './types';
import type { Patient, SessionWithPsychologist } from './data';
import type { DragState } from './types';
import { MouseUpParams } from './mouseHandlers';
import { isImpersonated } from '../../../hooks/api/auth/tokenManager';
import { timezoneToCountryCode } from '../../../utils/timezoneToCountry';
import CountryFlag from '../../common/CountryFlag';
import { needsCoordination } from './coordinationWindow';
import { useContactChannel } from '../../../hooks/useContactChannel';
import { buildWhatsappLink } from '../../../utils/contactChannel';
import type { VacationSegment } from '../../../hooks/useVacationBlockade';
import { VacationSegmentTooltip } from './VacationSegmentTooltip';

// Inyectar keyframes para animaciones (una sola vez)
if (typeof document !== 'undefined' && !document.getElementById('psi-shake-style')) {
  const style = document.createElement('style');
  style.id = 'psi-shake-style';
  style.textContent = `@keyframes psi-shake{0%,100%{transform:translateX(0)}15%{transform:translateX(-3px)}30%{transform:translateX(3px)}45%{transform:translateX(-2px)}60%{transform:translateX(2px)}75%{transform:translateX(-1px)}90%{transform:translateX(1px)}}@keyframes psi-spin{to{transform:rotate(360deg)}}`;
  document.head.appendChild(style);
}

interface CalendarCellProps {
  day: { date: string };
  idx: number;
  ts: string;
  rowIdx: number;
  sessionsInSlot: SessionWithPsychologist[];
  hasSession: boolean;
  hasOverlapAt: ( date: string, start: number, end: number) => boolean;
  template: { id: string; weekday: number; startTime: string; endTime: string; active: boolean }[];
  hasScheduleCell: boolean;
  startOfWeek: Date;
  endOfWeek: Date;
  now: Date;
  selectedTimeZone: string;
  editMode: boolean;
  isBusy: boolean;
  blockAll: boolean;
  blockScheduling: boolean;
  disableBooking: boolean;
  dragging: DragState | null;
  dragVisual: DragVisual | null;
  dragBlocked: boolean;
  visibleTimeSlots: string[];
  ROW_HEIGHT_PX: number;
  QUARTER_HEIGHT_PX: number;
  patients: Patient[];
  psychologist: string | null;
  tooltipTimerRef: React.MutableRefObject<number | null>;
  gridRef: React.RefObject<HTMLDivElement>;
  availability: AvailabilityDay[];
  setHoveredCell: (cell: { date: string; time: string } | null) => void;
  setTooltip: (tooltip: { rect: DOMRect; content: string; date: string; time: string } | null) => void;
  setSelectedRangeId: (id: string | number | null) => void;
  setTemplateModal: (modal: { date: string; startTime: string; endTime: string; weekdayIdx: number } | null) => void;
  setDragModal: (modal: { date: string; startTime: string; endTime: string } | null) => void;
  handleMouseDown: (params: { date: string; timeSlot: string; weekdayIdx: number; gridRect: DOMRect; headerHeight: number; availabilityLength: number; clientY: number; adjustedStart: string; scrollTop: number; gridClientWidth: number }) => void;
  handleCellMouseUp: (params: MouseUpParams) => { shouldReset: boolean; templateModal?: { date: string; startTime: string; endTime: string; weekdayIdx: number } | null; dragModal?: { date: string; startTime: string; endTime: string } | null };
  resetDrag: () => void;
  handleSessionClick?: (session: SessionWithPsychologist) => void;
  handleSessionUnblock?: (psychologistId: string, fromDate: Date, toDate: Date, sesionId: string, blockageIds?: string[]) => void;
  isDayBlocked?: boolean;
  isSchedulingMode?: boolean;
  availableSlots?: { slot00?: { startTime: string; endTime: string; isOpen: boolean }; slot30?: { startTime: string; endTime: string; isOpen: boolean } } | null;
  selectedPatient?: { name?: string; email: string; timezone?: string } | null;
  scheduleCoverage?: { from: number; to: number; rescheduleOnly?: boolean }[] | null;
  reschedulingSessionId?: string | null;
  deletedScheduleIds?: Set<string | number>;
  dateSpecificRange?: { date: string; startTime: string; endTime: string; scheduleType?: 'standard' | 'reschedule_only' } | null;
  onDateSpecificClick?: (range: { date: string; startTime: string; endTime: string; scheduleType?: 'standard' | 'reschedule_only' }) => void;
  blockedCoverage?: { from: number; to: number } | null;
  blockedSessionInSlot?: SessionWithPsychologist | null;
  unblockingSessionId?: string | null;
  /** Tramo de bloqueo por vacaciones (real / previo / posterior) que cubre este día, si aplica. */
  vacationSegment?: VacationSegment | null;
  onVacationSegmentClick?: () => void;
}

/** Cuánto puede moverse el dedo y seguir siendo un TOQUE; más que eso es un arrastre (scroll de la grilla). */
const TOUCH_TAP_SLOP_PX = 10;

const CalendarCell: React.FC<CalendarCellProps> = memo(({
  day,
  idx,
  ts,
  rowIdx,
  template,
  hasScheduleCell,
  startOfWeek,
  endOfWeek,
  now,
  selectedTimeZone,
  editMode,
  isBusy,
  blockAll,
  blockScheduling,
  disableBooking,
  dragging,
  sessionsInSlot,
  hasSession,
  hasOverlapAt,
  visibleTimeSlots,
  ROW_HEIGHT_PX,
  QUARTER_HEIGHT_PX,
  patients,
  psychologist,
  tooltipTimerRef,
  gridRef,
  availability,
  setHoveredCell,
  setTooltip,
  setSelectedRangeId,
  setTemplateModal,
  setDragModal,
  handleMouseDown,
  handleCellMouseUp,
  resetDrag,
  handleSessionClick,
  handleSessionUnblock,
  dragVisual,
  dragBlocked,
  isDayBlocked = false,
  vacationSegment = null,
  onVacationSegmentClick,
  isSchedulingMode = false,
  availableSlots = null,
  selectedPatient = null,
  scheduleCoverage = null,
  reschedulingSessionId = null,
  deletedScheduleIds,
  dateSpecificRange = null,
  onDateSpecificClick,
  blockedCoverage = null,
  blockedSessionInSlot = null,
  unblockingSessionId = null,
}) => {
  const [hoveredHalf, setHoveredHalf] = useState<'top' | 'bottom' | null>(null);
  const [showWhatsAppPopover, setShowWhatsAppPopover] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [shakeExpired, setShakeExpired] = useState(false);
  // Gesto táctil en curso sobre esta celda: distingue un TOQUE de un ARRASTRE (scroll). Solo un toque elige el horario.
  const touchRef = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  // NZ, u O que viene de NZ: único canal es administración/NZ, nunca el WhatsApp de CX-F.
  const { whatsappNumber: contactWhatsappNumber } = useContactChannel();

  // Sesión "principal" del slot: se usa para tooltip, click de celda y cálculos de
  // disponibilidad remanente. Cuando hay más de una sesión solapada en el mismo slot,
  // todas se renderizan (ver bloque de SessionItem más abajo), pero estos cálculos
  // auxiliares solo necesitan una referencia.
  const sessionInSlot = sessionsInSlot[0] ?? null;
  // Bloqueos parciales de esta celda. Fuera del modo agendar cuentan como sesión (`hasSession`); en modo
  // agendar la grilla no los cuenta (se puede agendar encima) pero se siguen dibujando, sin recibir mouse.
  const hasBlockedItems = sessionsInSlot.some(s => s.status === 'blocked');
  const psychologistId = psychologist ? psychologist : 'no-psychologist';
  const [yy, mm, dd] = day.date.split('-').map(Number);
  const date = new Date(yy, mm - 1, dd);
  // On desktop idx === absolute weekday (all 7 days in availability array).
  // On mobile only a 3-4 day slice is passed, so idx is relative (0,1,2,3).
  // template.weekday / weekdayIdx consumers need the absolute weekday, derived
  // from day.date, to avoid a day offset on mobile (see WeeklyPsychologistCalendar renderCell).
  const jsDay = date.getDay();
  const weekdayAbsIdx = jsDay === 0 ? 6 : jsDay - 1;
  const today0 = new Date();
  today0.setHours(0, 0, 0, 0);
  const tomorrow0 = new Date(today0.setDate(today0.getDate() + 1));
  const isBeforeTomorrow = isImpersonated() ? false : date < tomorrow0;
  const start0 = new Date(startOfWeek); start0.setHours(0, 0, 0, 0);
  const end0 = new Date(endOfWeek); end0.setHours(23, 59, 59, 999);
  const isCurrentWeek = start0 <= today0 && today0 <= end0;
  const isPastDay = (editMode ? false : (isCurrentWeek && date < tomorrow0));
  const tzNow = getNowInSelectedTZParts(selectedTimeZone);
  const isToday = day.date === tzNow.todayStr;
  const isCurrentHour = isToday && ts.slice(0, 2) === tzNow.hour;
  const logicalHour = now.getHours();
  const slotHour = Number(ts.slice(0, 2));
  const isPastHour = isToday && slotHour < logicalHour;
  const isWithinCurrentHour = isToday && slotHour === now.getHours();
  const rowStartMin = timeToMinutes(ts);
  const rowEndMin = rowStartMin + 60;
  // Disponibilidad: se pinta como div hijo posicionado (no como bg de la celda)
  const isAvailabilityCell = !editMode && hasScheduleCell && !hasSession && !isDayBlocked;
  // Calcular si el slot está en el pasado, dentro de las próximas 24h, o más allá
  const slotMinutes = ts.includes(':') ? Number(ts.slice(3, 5)) : 0;
  const cellDateTime = new Date(yy, mm - 1, dd, slotHour, slotMinutes, 0);
  const isCellPast = cellDateTime < now;
  const isCellWithin24h = !isImpersonated() && needsCoordination(cellDateTime, now);
  const isPastAvailability = isAvailabilityCell && isCellPast;
  const isBlocked24h = isAvailabilityCell && isCellWithin24h;
  const isFutureAvailability = isAvailabilityCell && !isCellPast && !isCellWithin24h;
  const bg = editMode ? '#ffffff'
    : isCellPast ? '#D0D0D0'
    : '#F5F5F5';

  // Determine which slot to show on hover based on cursor Y position
  const hoveredSlot = hoveredHalf === 'top' ? availableSlots?.slot00 : hoveredHalf === 'bottom' ? availableSlots?.slot30 : null;
  // Fallback: if the selected half has no slot, try the other
  const activeHoverSlot = hoveredSlot || (hoveredHalf === 'top' ? availableSlots?.slot30 : hoveredHalf === 'bottom' ? availableSlots?.slot00 : null);
  // Which visual half to render the preview in: use the actual slot's offset, not just cursor position
  const previewHalf: 'top' | 'bottom' | null = !activeHoverSlot ? null
    : hoveredSlot ? hoveredHalf // direct match → use cursor half
    : (hoveredHalf === 'top' ? 'bottom' : 'top'); // fallback → show in the other half
  // El preview debe ocupar el alto real del turno (60min individual, 90min pareja/familia),
  // no una fila fija — se calcula a partir del propio rango del slot disponible.
  const previewDurationMin = activeHoverSlot
    ? timeToMinutes(activeHoverSlot.endTime) - timeToMinutes(activeHoverSlot.startTime)
    : 60;
  const previewHeightPercent = (previewDurationMin / 60) * 100;

  // Patient timezone display
  const patientTzInfo = (() => {
    if (!activeHoverSlot || !selectedPatient?.timezone || !isSchedulingMode) return null;
    try {
      const utcDate = localTzToUTC(day.date, activeHoverSlot.startTime, selectedTimeZone);
      const patientParts = isoToZoneParts(utcDate.toISOString(), selectedPatient.timezone);
      const utcEndDate = localTzToUTC(day.date, activeHoverSlot.endTime, selectedTimeZone);
      const patientEndParts = isoToZoneParts(utcEndDate.toISOString(), selectedPatient.timezone);
      const iso2 = timezoneToCountryCode(selectedPatient.timezone);
      const city = selectedPatient.timezone.split('/').pop()?.replace(/_/g, ' ') ?? '';
      return { timeRange: `${patientParts.time} - ${patientEndParts.time}`, city, iso2 };
    } catch { return null; }
  })();
  // Líneas divisorias: vertical blanca 2px, horizontal gris oscura 2px
  const vSep = editMode ? '2px solid #E3E3E3' : '2px solid rgb(224, 224, 224)';
  const hSep = editMode ? '2px solid #E3E3E3' : '2px solid #bfbfbf';
  // Deshabilitar interacción si el día está bloqueado por recurrencia
  const isClickable = !isDayBlocked && (!editMode && !isBusy && !blockAll && !isBeforeTomorrow && !blockScheduling) && ((hasScheduleCell && !disableBooking) || hasSession);
  // Si hay sesión en la celda pero termina antes del final, el resto de la celda sigue disponible
  const sessionEndMin = sessionInSlot ? timeToMinutes(sessionInSlot.time) + (sessionInSlot.duration || 60) : null;
  const slot30Available = !!(availableSlots?.slot30 && !availableSlots?.slot00 && sessionEndMin !== null && sessionEndMin <= rowStartMin + 30);
  // Fracción dentro de la celda en que termina la sesión (ej: 0.5 si termina en :30)
  const sessionEndFraction = (hasSession && sessionEndMin !== null && sessionEndMin > rowStartMin && sessionEndMin < rowStartMin + 60)
    ? (sessionEndMin - rowStartMin) / 60
    : null;
  const hasAvailableSlots = isSchedulingMode && availableSlots && (availableSlots.slot00 || availableSlots.slot30) && (!hasSession || slot30Available);
  const isRestrictedSchedulingSlot = isSchedulingMode && !hasSession && (isCellPast || isCellWithin24h);
  const cursorStyle = isDayBlocked ? 'not-allowed' as const
    : isRestrictedSchedulingSlot
      ? ((isCellPast && !isImpersonated()) ? 'not-allowed' as const : 'pointer' as const)
      : ((hasSession && !hasAvailableSlots) ? 'default' as const : (isClickable || hasAvailableSlots ? 'pointer' as const : 'default' as const));
  
  return (
    <div
      key={`${day.date}-${ts}`}
      data-grid-cell="true"
      data-time-slot={ts}
      data-day-index={idx}
      style={{ padding: '0.5rem', borderRight: vSep, borderLeft: idx === 0 ? vSep : undefined, borderBottom: hSep, borderTop: rowIdx === 0 ? hSep : undefined, minHeight: `${ROW_HEIGHT}rem`, background: bg, cursor: cursorStyle, position: 'relative', opacity: isDayBlocked ? 0.7 : 1, pointerEvents: isDayBlocked ? 'none' : 'auto', overflow: 'visible' }}
      onMouseEnter={(e) => {
        setIsHovered(true);
        if (tooltipTimerRef.current) window.clearTimeout(tooltipTimerRef.current);
        if (dragging || isBusy || isDayBlocked) return;
        
        setHoveredCell({ date: day.date, time: ts });
        
        const targetElement = e.currentTarget as HTMLElement;
        if (!targetElement) return;
        
        tooltipTimerRef.current = window.setTimeout(() => {
          if (!targetElement.isConnected) return;
          
          let tooltipContent = '';
          
          if (editMode) {
            // No tooltip in schedule-config mode
          } else {
            if (hasSession && sessionInSlot?.status === "blocked") {
              // Bloqueo personal (ej. evento externo tipo "Turno médico") — no es una sesión
              // con paciente, así que el tooltip no debe hablar de "sesión pendiente/programada".
              tooltipContent = `🚫 Bloqueo: ${sessionInSlot?.patient.name || 'Horario bloqueado'}.\nHaz clic para ver detalles.`;
            } else if (hasSession) {
              const isPending = sessionInSlot?.status === "incomplete";
              const sessionLabel = sessionInSlot?.status === "active" || sessionInSlot?.status === "scheduled" ? "programada" : "pendiente";
              let paymentNote = '';
              if (isPending && sessionInSlot?.date && sessionInSlot?.time) {
                const start = parseBackendSessionDate(sessionInSlot.origISO || `${sessionInSlot.date} ${sessionInSlot.time}:00`, BA_TIMEZONE);
                const cd = getPaymentCountdown(start, selectedTimeZone);
                if (!cd.isExpired) {
                  paymentNote = `\n⏱ ${cd.cancellationLabel}`;
                }
              }
              tooltipContent = `📅 Sesión ${sessionLabel} con ${sessionInSlot?.patient.name || 'paciente'}.${paymentNote}\nHaz clic para ver detalles.`;
            } else if (hasScheduleCell) {
              // if (isCellPast) {
              //   tooltipContent = `🚫 Este horario ya expiró.\nNo se pueden agendar sesiones en horarios pasados.`;
              // } else if (isCellWithin24h) {
              //   tooltipContent = `⏰ No se puede agendar con menos de 24hs de anticipación.\nHacé clic para contactar a CX por WhatsApp.`;
              // } else {
              //   tooltipContent = `✓ Horario disponible para agendar sesión.\nHaz clic para agendar.`;
              // }
            }
          }

          if (tooltipContent) {
            const [y, m, d] = day.date.split("-");
            const formattedDate = `${d}/${m}/${y}`;
            const sessionTime = hasSession && sessionInSlot?.time ? sessionInSlot.time : ts;
            setTooltip({
              rect: targetElement.getBoundingClientRect(),
              content: tooltipContent,
              date: formattedDate,
              time: sessionTime
            });
          }
        }, 1750);
      }}
      onMouseLeave={() => {
        if (tooltipTimerRef.current) {
          window.clearTimeout(tooltipTimerRef.current);
          tooltipTimerRef.current = null;
        }
        setTooltip(null);
        setHoveredCell(null);
        setHoveredHalf(null);
        setIsHovered(false);
        setShowWhatsAppPopover(false);
      }}
      onMouseMove={(e) => {
        // Track cursor Y position for :00/:30 slot selection
        if (isSchedulingMode && availableSlots && (!hasSession || slot30Available) && !dragging) {
          const cellRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          const yInCell = e.clientY - cellRect.top;
          const half = yInCell < (cellRect.height / 2) ? 'top' : 'bottom';
          if (half !== hoveredHalf) setHoveredHalf(half);
        }
      }}
      onTouchStart={(e) => {
        // Solo se anota dónde empezó el gesto. NO se muestra ningún horario todavía: en un celular tocar la grilla para
        // deslizarse (scroll) no es elegir un horario, y como un dedo no dispara `mouseleave` el "posible horario" de cada
        // celda tocada se quedaba pegado y se acumulaban. El horario se decide en el click (un toque real).
        const touch = e.touches[0];
        touchRef.current = { x: touch.clientX, y: touch.clientY, moved: false };
        if (hoveredHalf) setHoveredHalf(null);
      }}
      onTouchMove={(e) => {
        const start = touchRef.current;
        if (!start || start.moved) return;
        const touch = e.touches[0];
        if (Math.abs(touch.clientX - start.x) > TOUCH_TAP_SLOP_PX || Math.abs(touch.clientY - start.y) > TOUCH_TAP_SLOP_PX) {
          start.moved = true;
        }
      }}
      onTouchCancel={() => { touchRef.current = null; }}
      onClick={async (e) => {
        // Toque (no mouse): si el dedo se arrastró no es un click. Si fue un toque, el horario sale de DÓNDE tocó
        // (en el mouse sale del hover, que en el celular no existe).
        const touch = touchRef.current;
        touchRef.current = null;
        if (touch?.moved) return;
        let clickSlot = activeHoverSlot;
        if (touch && availableSlots) {
          const cellRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          const half = (e.clientY - cellRect.top) < (cellRect.height / 2) ? 'top' : 'bottom';
          const direct = half === 'top' ? availableSlots.slot00 : availableSlots.slot30;
          const fallback = half === 'top' ? availableSlots.slot30 : availableSlots.slot00;
          clickSlot = direct || fallback || null;
        }
        if (isDayBlocked) return;
        if (tooltipTimerRef.current) {
          window.clearTimeout(tooltipTimerRef.current);
          tooltipTimerRef.current = null;
        }
        setTooltip(null);
        setHoveredCell(null);

        // Shake animation on expired cells in scheduling mode (impersonado puede agendar en pasado)
        if (isSchedulingMode && !hasSession && isCellPast && !isImpersonated()) {
          setShakeExpired(true);
          setTimeout(() => setShakeExpired(false), 500);
          return;
        }
        // Show WhatsApp popover for within-24h cells in scheduling mode
        if (isSchedulingMode && !hasSession && isCellWithin24h) {
          setShowWhatsAppPopover(prev => !prev);
          return;
        }
        // Click en slot disponible (scheduling mode)
        if (clickSlot && isSchedulingMode && (!hasSession || slot30Available)) {
          setDragModal({ date: day.date, startTime: clickSlot.startTime, endTime: clickSlot.endTime });
          return;
        }
        // Impersonado puede clickear sesiones/celdas en días u horarios pasados (para reagendar etc.)
        const blockedByPast = !isImpersonated() && (isPastDay || isPastHour || isWithinCurrentHour);
        if (isDayBlocked || editMode || isBusy || blockAll || blockScheduling || blockedByPast) return;
        if (reschedulingSessionId) return;
        if (hasSession && handleSessionClick && sessionInSlot) handleSessionClick(sessionInSlot);
      }}
      onMouseDown={(e) => {
        if (isDayBlocked) return;
        if (!editMode) return;
        e.preventDefault();
        if (isBusy || blockAll) return;
        if (tooltipTimerRef.current) {
          window.clearTimeout(tooltipTimerRef.current);
          tooltipTimerRef.current = null;
        }
        setTooltip(null);
        setHoveredCell(null);
        
        const gridRect = gridRef.current?.getBoundingClientRect();
        if (!gridRect) return;
        const firstRowEl = gridRef.current?.querySelector('[data-first-time-row="true"]') as HTMLDivElement | null;
        const headerHeight = firstRowEl ? (firstRowEl.getBoundingClientRect().top - gridRect.top) : 0;
        let y = e.clientY - gridRect.top;
        y = Math.max(0, Math.min(y, gridRect.height));
        let yAdj = y - headerHeight;
        yAdj = Math.max(0, Math.min(yAdj, gridRect.height - headerHeight));
        const rowIdxFromAdj = Math.floor(yAdj / ROW_HEIGHT_PX);
        const posInRow = yAdj - rowIdxFromAdj * ROW_HEIGHT_PX;
        const quarterIndex = Math.floor(Math.max(0, Math.min(posInRow, ROW_HEIGHT_PX - 1)) / QUARTER_HEIGHT_PX);
        const baseHourStr = ts.split(':')[0];
        const minuteStr = ['00', '30'][quarterIndex] || '00';
        const adjustedStart = `${baseHourStr}:${minuteStr}`;
        setSelectedRangeId(null);
        handleMouseDown({ date: day.date, timeSlot: ts, weekdayIdx: idx, gridRect, headerHeight, availabilityLength: availability.length, clientY: e.clientY, adjustedStart, scrollTop: gridRef.current?.scrollTop || 0, gridClientWidth: gridRef.current?.clientWidth || gridRect.width });
      }}
      onMouseUp={(e) => {
        if (isDayBlocked) return;
        if (!editMode) return;
        e.preventDefault();
        if (!dragging || !dragVisual || !gridRef.current) return;
        if (dragBlocked) {
          resetDrag();
          return;
        }
        const rect = gridRef.current.getBoundingClientRect();
        if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
          resetDrag();
          return;
        }
        const filteredTemplate = deletedScheduleIds
          ? template.filter(t => !deletedScheduleIds.has(t.id))
          : template;
        const result = handleCellMouseUp({
          dragging,
          dragVisual,
          dragBlocked,
          hasOverlapAt,
          editMode,
          template: filteredTemplate,
          startOfWeek,
          psychologistId: psychologistId,
          now,
          selectedTimeZone,
          visibleTimeSlots,
          gridRef,
          clientX: e.clientX,
          clientY: e.clientY,
          dayDate: day.date,
          dayIdx: weekdayAbsIdx,
          rowHeightPx: ROW_HEIGHT_PX,
          quarterHeightPx: QUARTER_HEIGHT_PX
        });
        if (result.shouldReset) resetDrag();
        if (result.templateModal) setTemplateModal(result.templateModal);
        if (result.dragModal) setDragModal(result.dragModal);
      }}
    >
      {/* Bloqueo por días off REALES (no el padding previo/posterior — ese solo se marca con el
          cartel de la fila superior): gris consistente en toda la celda, independiente de si hay
          o no slots de disponibilidad marcados — antes esta franja solo se veía cuando el backend
          devolvía slots OCCUPIED_BY_VACATION, lo cual era inconsistente día a día. Debajo de
          SessionItem (zIndex 35/36) para no tapar una sesión real si existiera. */}
      {vacationSegment?.type === 'vacation' && (
        isSchedulingMode ? (
          // En modo agendamiento/reprogramación el click de la celda debe primar sobre la
          // navegación a "gestionar día off" (la psicóloga puede agendar en sus días off si
          // quiere) — se mantiene el tinte gris a modo informativo pero sin interceptar clicks,
          // dejando pasar el mouse hasta la lógica normal de la celda (hover de slot, click de
          // agendar, etc.) definida más abajo.
          <div
            data-testid="calendar-cell-vacation-overlay"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(55, 65, 81, 0.35)',
              pointerEvents: 'none',
              zIndex: 4,
            }}
          />
        ) : (
          <VacationSegmentTooltip segment={vacationSegment}>
            <button
              type="button"
              data-testid="calendar-cell-vacation-overlay"
              // stopPropagation: este overlay es puramente informativo — no debe disparar la
              // lógica de click/drag de la celda (agendar, drag-to-select horarios, etc.) por
              // más que un click bubblee desde este botón hacia el div padre de la celda.
              onClick={(e) => { e.stopPropagation(); onVacationSegmentClick?.(); }}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(55, 65, 81, 0.35)',
                border: 'none',
                padding: 0,
                cursor: onVacationSegmentClick ? 'pointer' : 'default',
                // explícito: el div raíz de la celda pone pointerEvents 'none' cuando
                // isDayBlocked (mecanismo de bloqueo full-day aparte del de vacaciones) — sin
                // este override el hover de este overlay heredaría 'none' y jamás dispararía.
                pointerEvents: 'auto',
                zIndex: 4,
              }}
            />
          </VacationSegmentTooltip>
        )
      )}
      {/* Disponibilidad: divs posicionados que cubren cada segmento */}
      {scheduleCoverage && scheduleCoverage.length > 0 && (isFutureAvailability || isPastAvailability || isBlocked24h || (isSchedulingMode && (isCellPast || isCellWithin24h)) || (sessionEndFraction !== null && !editMode && !isCellPast && !isCellWithin24h)) && scheduleCoverage.map((seg, segIdx) => {
        const clippedFrom = sessionEndFraction !== null ? Math.max(seg.from, sessionEndFraction) : seg.from;
        if (clippedFrom >= seg.to) return null;
        return (
          <div key={segIdx} data-testid="schedule-seg" style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: `${clippedFrom * 100}%`,
            height: `${(seg.to - clippedFrom) * 100}%`,
            background: (isPastAvailability || isBlocked24h || (isSchedulingMode && (isCellPast || isCellWithin24h)))
              // Pasado o bloqueado (próximas 24h): rayas finas en tono gris neutro
              ? 'repeating-linear-gradient(-45deg, transparent, transparent 3px, rgba(128, 128, 128, 0.2) 3px, rgba(128, 128, 128, 0.2) 4px), rgba(128, 128, 128, 0.1)'
              // Futuro: ámbar para reagendamiento, verde para estándar
              : seg.rescheduleOnly
                ? 'rgba(74, 61, 115, 0.22)'
                : 'rgba(16, 185, 129, 0.30)',
            pointerEvents: 'none',
            zIndex: 0,
          }} />
        );
      })}
      {/* Overlay gris sólido en modo agendamiento para horarios dentro de 24h o pasados (solo para slots SIN rangos configurados) */}
      {isSchedulingMode && (isCellPast || isCellWithin24h) && !hasSession && (!scheduleCoverage || scheduleCoverage.length === 0) && (
        <div style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          height: '100%',
          background: 'rgba(128, 128, 128, 0.15)',
          pointerEvents: 'none',
          zIndex: 5,
        }} />
      )}
      {/* Indicador visual al hacer hover en celdas restringidas (scheduling mode).
          Impersonando: omitimos este overlay para que se muestre el preview verde de agendar normal. */}
      {(isHovered || shakeExpired) && isSchedulingMode && !hasSession && (isCellPast || isCellWithin24h) && !showWhatsAppPopover && !isImpersonated() && (
        <div style={{
          position: 'absolute',
          left: 3,
          right: 3,
          top: 3,
          bottom: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: isCellPast
            ? (shakeExpired ? '1.5px solid rgba(239, 68, 68, 0.5)' : '1.5px dashed rgba(156, 163, 175, 0.6)')
            : '1.5px dashed rgba(245, 158, 11, 0.5)',
          borderRadius: 6,
          background: shakeExpired ? 'rgba(239, 68, 68, 0.06)' : 'transparent',
          pointerEvents: 'none',
          zIndex: 20,
          animation: shakeExpired ? 'psi-shake 0.4s ease-in-out' : undefined,
        }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.2rem',
            fontSize: '0.6rem',
            fontWeight: shakeExpired ? 600 : 500,
            color: isCellPast ? (shakeExpired ? '#dc2626' : '#9ca3af') : '#d97706',
            whiteSpace: 'nowrap',
            lineHeight: 1,
            letterSpacing: '0.02em',
            transition: 'color 0.2s ease',
          }}>
            {isCellPast ? 'Fecha pasada' : 'Requiere coordinación'}
            {!isCellPast && (
              <Tooltip delayDuration={150}>
                <TooltipTrigger asChild>
                  <span
                    data-testid="requires-coordination-info-btn"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: '#d97706',
                      opacity: 0.75,
                      cursor: 'default',
                      pointerEvents: 'auto',
                      flexShrink: 0,
                    }}
                    aria-label="¿Por qué requiere coordinación?"
                  >
                    <Info size={9} />
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  sideOffset={8}
                  className="w-[280px] py-3 space-y-2 text-left leading-relaxed"
                >
                  <p className="font-semibold text-foreground text-[0.8125rem]">¿Por qué requiere coordinación?</p>
                  <p className="text-xs text-muted-foreground">
                    Estás intentando agendar una sesión con menos de 24 horas de anticipación. En estos casos no es posible hacerlo directamente: necesitás contactarte con el equipo de CX, indicarles que querés agendar con ese paciente dentro de las próximas 24hs, y ellos se encargan de coordinar el agendamiento.
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </span>
        </div>
      )}
      {(() => {
        // Check if day is in the past
        const [yyyy, mm, dd] = day.date.split('-').map(Number);
        const dayDate = new Date(yyyy, mm - 1, dd);
        dayDate.setHours(0, 0, 0, 0);
        const today = new Date(now);
        today.setHours(0, 0, 0, 0);
        const isPastDay = dayDate < today;
        
        // In scheduling mode (not editMode), don't show ranges for past days
        if (!editMode && isPastDay) {
          return null;
        }
        
        return (
          <ScheduleOverlay
            weekdayIdx={weekdayAbsIdx}
            rowStartMin={rowStartMin}
            rowEndMin={rowEndMin}
            template={template}
            editMode={editMode}
            deletedScheduleIds={deletedScheduleIds}
          />
        );
      })()}
      {/* Blocked session: per-cell striped overlay — clickable to open unblock modal */}
      {blockedCoverage && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: `${blockedCoverage.from * 100}%`,
            height: `${(blockedCoverage.to - blockedCoverage.from) * 100}%`,
            background: `repeating-linear-gradient(-45deg, rgba(212, 208, 224, 1), rgba(212, 208, 224, 1) 5px, #e5e2ef 5px, #e5e2ef 10px)`,
            // En modo agendar el bloqueo no captura el mouse: el click y el hover llegan a la celda
            // para poder agendar encima (el desbloqueo solo se ofrece fuera de ese modo).
            pointerEvents: blockedSessionInSlot && handleSessionUnblock && !isSchedulingMode ? 'auto' : 'none',
            cursor: blockedSessionInSlot && handleSessionUnblock && !isSchedulingMode ? 'pointer' : 'default',
            zIndex: 2,
          }}
          onClick={(e) => {
            if (!blockedSessionInSlot || !handleSessionUnblock || isSchedulingMode) return;
            e.stopPropagation();
            const start = new Date(`${blockedSessionInSlot.date}T${blockedSessionInSlot.time}`);
            const end = new Date(start.getTime() + (blockedSessionInSlot.duration || 60) * 60 * 1000);
            handleSessionUnblock(psychologistId, start, end, blockedSessionInSlot.id, blockedSessionInSlot.blockageIds);
          }}
        />
      )}
      {/* Blocked session: loader overlay while unblock is in progress */}
      {blockedCoverage && blockedSessionInSlot && unblockingSessionId === blockedSessionInSlot.id && (
        <div style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: `${blockedCoverage.from * 100}%`,
          height: `${(blockedCoverage.to - blockedCoverage.from) * 100}%`,
          background: 'rgba(255,255,255,0.55)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          zIndex: 40,
        }}>
          <div style={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            border: '2.5px solid #d1c4f5',
            borderTopColor: '#6D57BC',
            animation: 'psi-spin 0.7s linear infinite',
          }} />
        </div>
      )}
      {/* Blocked session: label + unblock button anchored to first visible cell (hidden when a regular session occupies the slot) */}
      {blockedSessionInSlot && !hasSession && !hasBlockedItems && (() => {
        const sessionStartHour = parseInt(blockedSessionInSlot.time.slice(0, 2));
        const currentHour = parseInt(ts.slice(0, 2));
        const firstVisibleHour = parseInt(visibleTimeSlots[0]?.slice(0, 2) || '0');
        const renderFromHour = Math.max(sessionStartHour, firstVisibleHour);
        if (currentHour !== renderFromHour) return null;

        const clippedStart = Math.max(timeToMinutes(blockedSessionInSlot.time), rowStartMin);
        const topPx = Math.round(((clippedStart - rowStartMin) / 60) * ROW_HEIGHT_PX) + 1;

        return (
          <div style={{
            position: 'absolute',
            top: topPx,
            left: 0,
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
            padding: '4px 8px',
            color: '#292b30',
            fontSize: '0.75rem',
            fontWeight: 700,
            zIndex: 36,
            overflow: 'hidden',
            pointerEvents: 'none',
          }}>
            <span style={{ fontWeight: 700, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {blockedSessionInSlot.patient.name}
            </span>
            <div style={{ display: 'flex', justifyContent: 'left', alignItems: 'center', opacity: 0.9, fontSize: '0.7rem', fontWeight: 500 }}>
              <span>{blockedSessionInSlot.time}</span>
              <span style={{ marginLeft: '0.5rem' }}>🕒</span>
            </div>
          </div>
        );
      })()}
      {/* Date-specific range bubble — only visible in edit/config mode */}
      {editMode && dateSpecificRange && !hasSession && (() => {
        const rangeStart = timeToMinutes(dateSpecificRange.startTime);
        let rangeEnd = timeToMinutes(dateSpecificRange.endTime);
        if (rangeEnd === 0) rangeEnd = 24 * 60;
        const startHour = Math.floor(rangeStart / 60);
        const currentHourNum = parseInt(ts.slice(0, 2));
        const firstVisibleHour = parseInt(visibleTimeSlots[0]?.slice(0, 2) || '0');
        const renderFromHour = Math.max(startHour, firstVisibleHour);
        if (currentHourNum !== renderFromHour) return null;

        const clippedStart = Math.max(rangeStart, rowStartMin);
        const offsetInRow = clippedStart - rowStartMin;
        const offsetPx = Math.round((offsetInRow / 60) * ROW_HEIGHT_PX);
        const duration = rangeEnd - clippedStart;
        const heightPx = Math.max(20, Math.round((duration / 60) * ROW_HEIGHT_PX) - 2);
        const [, rm, rd] = dateSpecificRange.date.split('-');
        const dateLabel = editMode ? `${rd}/${rm}` : `${rd}/${rm}`;

        const isReschedule = dateSpecificRange.scheduleType === 'reschedule_only';
        return (
          <div style={{
            position: 'absolute',
            top: offsetPx + 1,
            left: 3,
            right: 3,
            height: heightPx,
            background: isReschedule
              ? `repeating-linear-gradient(
                  -45deg,
                  rgba(74, 61, 115, 0.06),
                  rgba(74, 61, 115, 0.06) 4px,
                  rgba(132, 117, 179, 0.12) 4px,
                  rgba(132, 117, 179, 0.12) 8px
                )`
              : `repeating-linear-gradient(
                  -45deg,
                  rgba(139, 92, 246, 0.06),
                  rgba(139, 92, 246, 0.06) 4px,
                  rgba(167, 139, 250, 0.12) 4px,
                  rgba(167, 139, 250, 0.12) 8px
                )`,
            border: isReschedule ? '1.5px dotted #4a3d73' : '1.5px dotted #a78bfa',
            borderRadius: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            color: isReschedule ? '#4a3d73' : '#6D57BC',
            pointerEvents: onDateSpecificClick ? 'auto' : 'none',
            cursor: onDateSpecificClick ? 'pointer' : 'default',
            zIndex: 12,
            overflow: 'hidden',
          }}
          onClick={onDateSpecificClick ? (e) => { e.stopPropagation(); onDateSpecificClick(dateSpecificRange); } : undefined}
          >
            <span style={{ fontSize: '0.65rem', fontWeight: 700, lineHeight: 1.2 }}>
              {dateLabel}
            </span>
            {heightPx > 30 && (
              <span style={{ fontSize: '0.6rem', fontWeight: 500, opacity: 0.8, lineHeight: 1 }}>
                {dateSpecificRange.startTime} - {dateSpecificRange.endTime}
              </span>
            )}
          </div>
        );
      })()}
      {isCurrentHour && <CurrentTimeIndicator now={now} rowHeightPx={ROW_HEIGHT_PX} />}
      {activeHoverSlot && previewHalf && (!hasSession || slot30Available) && isSchedulingMode && (isImpersonated() || (!isCellPast && !isCellWithin24h)) && (
        <div
          data-testid="calendar-cell-slot-preview"
          style={{
            position: 'absolute',
            left: 3,
            right: 3,
            ...(previewHalf === 'top'
              ? { top: 2, height: `calc(${previewHeightPercent}% - 4px)` }
              : { top: '50%', height: `calc(${previewHeightPercent}% - 2px)` }
            ),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            fontSize: '0.65rem',
            fontWeight: 700,
            color: activeHoverSlot.isOpen ? '#047857' : '#fff',
            background: activeHoverSlot.isOpen ? 'rgba(16, 185, 129, 0.1)' : 'rgba(48, 170, 148, 0.85)',
            border: activeHoverSlot.isOpen ? '1.5px dashed rgba(16, 185, 129, 0.4)' : '1.5px solid rgba(16, 185, 129, 0.5)',
            borderRadius: 4,
            cursor: 'pointer',
            // Por encima de un bloqueo (SessionItem, 35) sobre el que se puede agendar; no tapa nada más.
            zIndex: 36,
            transition: 'top 0.12s ease-out, height 0.12s ease-out, background 0.12s ease-out',
            pointerEvents: 'none',
            overflow: 'hidden',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CountryFlag timezone={selectedTimeZone} fallbackIso2="AR" size="0.65rem" />
            <span>{activeHoverSlot.startTime} - {activeHoverSlot.endTime}</span>
          </span>
          {patientTzInfo && (
            <span
              data-testid="calendar-cell-patient-tz"
              style={{
                fontSize: '0.55rem',
                fontWeight: 500,
                opacity: 0.8,
                color: activeHoverSlot.isOpen ? '#059669' : 'rgba(255,255,255,0.9)',
                lineHeight: 1.1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              {patientTzInfo.iso2 && <CountryFlag iso2={patientTzInfo.iso2} size="0.55rem" />}
              {patientTzInfo.timeRange} ({patientTzInfo.city})
            </span>
          )}
        </div>
      )}
      {/* Popover WhatsApp para slots dentro de las próximas 24h */}
      {showWhatsAppPopover && isSchedulingMode && isCellWithin24h && !hasSession && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% - 4px)',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 50,
            background: '#ffffff',
            borderRadius: 12,
            padding: '1rem 1.1rem',
            boxShadow: '0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
            minWidth: 240,
            maxWidth: 290,
            pointerEvents: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Flecha superior */}
          <div style={{
            position: 'absolute',
            top: -6,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 12,
            height: 6,
            overflow: 'hidden',
          }}>
            <div style={{
              width: 10,
              height: 10,
              background: '#ffffff',
              transform: 'rotate(45deg)',
              position: 'absolute',
              top: 3,
              left: 1,
              boxShadow: '0 -2px 6px rgba(0,0,0,0.06)',
            }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '1.25rem', lineHeight: 1, flexShrink: 0, marginTop: 1 }}>&#128337;</span>
            <div>
              <p style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1f2937', margin: 0, marginBottom: '0.25rem', lineHeight: 1.3 }}>
                Requiere coordinación
              </p>
              <p style={{ fontSize: '0.72rem', color: '#6b7280', margin: 0, lineHeight: 1.45 }}>
                No es posible agendar con menos de 24hs de anticipación. Podés contactar a CX para coordinarlo.
              </p>
            </div>
          </div>
          <a
            href={buildWhatsappLink(contactWhatsappNumber, `Hola, necesito coordinar una sesión para el ${day.date.split('-').reverse().join('/')} a las ${ts}hs Arg${selectedPatient?.name ? ` con el/la paciente ${selectedPatient.name}` : ''}.`)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.4rem',
              width: '100%',
              padding: '0.5rem 0.75rem',
              background: '#25D366',
              color: '#fff',
              borderRadius: 8,
              fontSize: '0.78rem',
              fontWeight: 700,
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#1fba59'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#25D366'; }}
            onClick={(e) => e.stopPropagation()}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Contactar a CX
          </a>
        </div>
      )}
      {(!isBusy && (hasSession || hasBlockedItems) && (!editMode || sessionInSlot?.status === 'blocked')) ? (() => {
        // Solo renderizar el SessionItem en la primera celda visible que la sesión cubre
        // Así al scrollear, la sesión se re-ancla a la primera celda visible en vez de desaparecer.
        // Si dos o más sesiones activas se solapan en el mismo slot (ej. mientras se resuelve
        // el bug de horarios pisados), todas anclan acá y se reparten el ancho de la celda en
        // columnas en vez de que solo una "gane" y tape a la otra.
        const currentHour = parseInt(ts.slice(0, 2));
        const firstVisibleHour = parseInt(visibleTimeSlots[0]?.slice(0, 2) || '0');

        const anchoredSessions = sessionsInSlot.filter(s => {
          const sessionStartHour = parseInt(s.time.slice(0, 2));
          const renderFromHour = Math.max(sessionStartHour, firstVisibleHour);
          return currentHour === renderFromHour;
        });
        if (anchoredSessions.length === 0) return null;

        const columnCount = anchoredSessions.length;

        return (
          <>
            {anchoredSessions.map((s, columnIndex) => (
              <SessionItem
                key={s.id}
                session={s}
                rowStartMin={rowStartMin}
                psychologistId={psychologistId}
                columnIndex={columnCount > 1 ? columnIndex : undefined}
                columnCount={columnCount > 1 ? columnCount : undefined}
                onClick={reschedulingSessionId ? undefined : () => { if (handleSessionClick) handleSessionClick(s); }}
                onUnblock={reschedulingSessionId ? undefined : handleSessionUnblock}
                inert={isSchedulingMode && s.status === 'blocked'}
                patients={patients}
                isBeingRescheduled={!!reschedulingSessionId && s.id === reschedulingSessionId}
                isPast={(() => {
                  // Calcular si está en el pasado o dentro de las próximas 24h
                  const now = new Date();
                  const nowPlus24h = new Date(now.getTime() + 24 * 60 * 60 * 1000);

                  // Construir fecha/hora de la sesión
                  const [year, month, day] = s.date.split('-').map(Number);
                  const [hour, minute] = s.time.split(':').map(Number);
                  const sessionDate = new Date(year, month - 1, day, hour, minute);

                  // Si la sesión está en el pasado o dentro de las próximas 24h, mostrar en gris
                  return sessionDate < nowPlus24h;
                })()}
              />
            ))}
          </>
        );
      })() : null}
    </div>
  );
});

CalendarCell.displayName = 'CalendarCell';

export default CalendarCell;

